import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { SignaturePad } from "../SignaturePad";
import { downloadAuthFile } from "../../lib/fileOpen";
import { fmtDateTime } from "./portalUtils";
import { SealCheck, Star, DownloadSimple, Images, FileText, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

function Stars({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n === value ? null : n)} className="p-0.5 transition-transform hover:scale-110" aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`${testId}-${n}`}>
          <Star size={20} weight={value >= n ? "fill" : "regular"} className={value >= n ? "text-amber-400" : "text-muted-foreground/50"} />
        </button>
      ))}
    </div>
  );
}

/* Final sign-off card on a completed portal job: review checklist, typed name, drawn signature, optional rating/comment. */
export function ClientSignOff({ job, onSigned }) {
  const { user } = useAuth();
  const rec = job.client_sign_off;
  const ev = job.sign_off_evidence || {};
  const [typedName, setTypedName] = useState(user?.name || "");
  const [signature, setSignature] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (rec) {
    return (
      <div className="rounded-lg border border-emerald-300 bg-emerald-50/70 dark:border-emerald-800 dark:bg-emerald-950/25 p-4" data-testid="sign-off-done">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5"><SealCheck size={16} weight="fill" /> Signed off by {rec.typed_name}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{fmtDateTime(rec.signed_at)} · verification code <span className="font-mono">{rec.verification_code}</span>{rec.rating ? ` · ${rec.rating}/5` : ""}</p>
            {rec.comment && <p className="text-xs mt-2 italic">“{rec.comment}”</p>}
          </div>
          <img src={rec.signature} alt="Signature" className="h-14 bg-white rounded border border-border px-2" data-testid="sign-off-signature-img" />
        </div>
        <Button size="sm" variant="outline" className="mt-3 h-7 text-xs" onClick={() => downloadAuthFile(`/api/client-hub/jobs/${job.id}/sign-off-certificate`, `${job.reference}_sign_off_certificate.pdf`)} data-testid="sign-off-certificate-btn">
          <DownloadSimple size={12} className="mr-1" /> Download sign-off certificate
        </Button>
      </div>
    );
  }
  if (job.status !== "completed") return null;
  if (!job.can_sign_off) {
    return <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-3" data-testid="sign-off-not-allowed">Final sign-off can be added by a portal user or administrator at your organisation.</p>;
  }

  const ready = signature && typedName.trim().length >= 2 && confirmed;
  const submit = async () => {
    setBusy(true);
    try {
      await API.post(`/client-hub/jobs/${job.id}/sign-off`, { typed_name: typedName.trim(), signature, confirmed, rating, comment });
      toast.success("Thank you — your sign-off has been recorded");
      onSigned?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border-2 border-emerald-400/70 bg-card p-4 space-y-4" data-testid="sign-off-card">
      <div>
        <p className="text-sm font-semibold flex items-center gap-1.5"><SealCheck size={16} className="text-emerald-600" /> Final sign-off</p>
        <p className="text-xs text-muted-foreground mt-0.5">Our team has marked this job complete. Review the evidence below, then sign to confirm you're happy with the work.</p>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center" data-testid="sign-off-evidence">
        {[["photos", Images, "photos"], ["documents", FileText, "documents"], ["forms", ClipboardText, "completed forms"]].map(([k, Icon, label]) => (
          <div key={k} className="rounded-md bg-muted/50 py-2">
            <Icon size={14} className="mx-auto text-muted-foreground" />
            <p className="text-sm font-bold font-[Chivo]">{ev[k] || 0}</p>
            <p className="text-[10px] text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">Your full name</label>
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full name" className="h-9 text-sm mt-1" data-testid="sign-off-name" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">How satisfied are you? <span className="opacity-70">(optional)</span></label>
          <div className="mt-1.5"><Stars value={rating} onChange={setRating} testId="sign-off-rating" /></div>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-medium text-muted-foreground">Draw your signature</label>
        <div className="mt-1" data-testid="sign-off-signature-pad"><SignaturePad value={signature} onChange={setSignature} width={420} height={140} /></div>
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Any comments for our team? (optional)" className="text-sm" data-testid="sign-off-comment" />
      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" data-testid="sign-off-confirm" />
        <span>I confirm I have reviewed the completion photos and documentation and the work has been completed to my satisfaction. This creates a digital signature recorded against the job.</span>
      </label>
      <Button onClick={submit} disabled={!ready || busy} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto" data-testid="sign-off-submit">
        <SealCheck size={14} className="mr-1.5" /> {busy ? "Recording…" : "Final sign-off"}
      </Button>
    </div>
  );
}
